"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  PcrComponentRows,
  type RowState,
} from "@/components/tools/biology-lab/pcr-component-rows";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type NumberFormatter,
  plain,
  toTsv,
} from "@/lib/tool-kit/biology-lab/format";
import {
  type MixError,
  type MixInput,
  type MixPlan,
  PCR_PRESETS,
  PCR_UNIT_LABELS,
  type PresetId,
  type PresetNameKey,
  planMasterMix,
  type RowIssue,
} from "@/lib/tool-kit/biology-lab/pcr-mix";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const PCR_MASTER_MIX_CALCULATOR_STRINGS = {
  presetLabel: "Start from a recipe",
  presetTaq: "Taq DNA polymerase, standard buffer (NEB M0273 values)",
  presetQ5: "Q5 High-Fidelity DNA polymerase (NEB M0491 values)",
  presetCustom: "Blank — enter my own reagents",
  nameBufferTaq: "10X Standard Taq reaction buffer",
  nameBufferQ5: "5X Q5 reaction buffer",
  nameDntps: "dNTP mix (each)",
  namePrimerFwd: "Forward primer",
  namePrimerRev: "Reverse primer",
  nameTaq: "Taq DNA polymerase",
  nameQ5: "Q5 High-Fidelity DNA polymerase",
  reactionVolume: "Reaction volume (µL)",
  reactions: "Number of reactions",
  reactionsHint: "Count your controls too.",
  overage: "Overage (%)",
  overageHint: "Extra mix to cover pipetting loss; 10% is a common choice.",
  template: "Template per tube (µL)",
  templateHint: "Added to each tube separately, not to the master mix.",
  reagentsHeading: "Reagents",
  colName: "Reagent",
  colStock: "Stock",
  colFinal: "Final",
  colUnit: "Unit",
  colPerReaction: "Per reaction (µL)",
  colMasterMix: "Master mix (µL)",
  remove: "Remove",
  addReagent: "Add a reagent",
  namePlaceholder: "Reagent name",
  results: "Results",
  masterMixTotal: "Master mix to make",
  aliquot: "Per tube",
  reactionsMade: "Reactions made for",
  unitUl: "µL",
  waterRow: "Nuclease-free water",
  totalRow: "Total (template not included)",
  recipeHeading: "Master mix recipe",
  stepsHeading: "At the bench",
  stepMake:
    "Combine the reagents and water in the table to make {total} µL of master mix, adding the polymerase last. Mix gently and keep it on ice.",
  stepDispense: "Dispense {aliquot} µL into each of the {count} tubes.",
  stepTemplate: "Add {template} µL of template to each tube.",
  stepNoTemplate: "Add the template to each tube to reach {volume} µL.",
  copy: "Copy recipe",
  copied: "Copied!",
  smallVolume:
    "{name} comes to {value} µL in the master mix — under 0.5 µL is hard to pipette accurately. Dilute the stock, or make a larger batch.",
  overfull:
    "The reagents and template add up to {over} µL more than the {volume} µL reaction, so there is no room for water. Use more concentrated stocks or a larger reaction volume.",
  issueStock: "{name}: enter a stock concentration greater than 0.",
  issueFinal: "{name}: enter a final concentration of 0 or more.",
  issueExceeds:
    "{name}: the final concentration is higher than the stock, which cannot be made by diluting it.",
  unnamed: "A reagent",
  sameUnitNote:
    "Use the same unit for a reagent's stock and final concentration; the volume per reaction is final ÷ stock × reaction volume.",
  errVolume: "Enter a reaction volume greater than 0 µL.",
  errReactions: "Enter at least 1 reaction.",
  errOverage: "The overage must be 0% or more.",
  errTemplate: "The template volume must be 0 µL or more.",
  enter:
    "Enter the reaction volume, the number of reactions and the template volume.",
  noReagents: "Add at least one reagent to build the master mix.",
} as const;

type Strings = typeof PCR_MASTER_MIX_CALCULATOR_STRINGS;

const ID = "pcr";
const SMALL_MIX_UL = 0.5;
const DECIMALS = 3;
const NAME_KEYS: Record<PresetNameKey, keyof Strings> = {
  bufferTaq: "nameBufferTaq",
  bufferQ5: "nameBufferQ5",
  dntps: "nameDntps",
  primerFwd: "namePrimerFwd",
  primerRev: "namePrimerRev",
  taq: "nameTaq",
  q5: "nameQ5",
};

function vol(fmt: NumberFormatter, value: number): string {
  return fmt(value, { maximumFractionDigits: DECIMALS });
}

function buildRows(
  preset: PresetId,
  strings: Strings,
  fmt: NumberFormatter
): RowState[] {
  return PCR_PRESETS[preset].components.map((component, index) => ({
    id: `p${index}`,
    name: String(strings[NAME_KEYS[component.nameKey]]),
    stock: plain(fmt, component.stock),
    final: plain(fmt, component.final),
    unit: component.unit,
  }));
}

function errorText(code: MixError, strings: Strings): string {
  const messages: Record<MixError, string> = {
    volume: strings.errVolume,
    reactions: strings.errReactions,
    overage: strings.errOverage,
    template: strings.errTemplate,
  };
  return messages[code];
}

function issueText(issue: RowIssue, name: string, strings: Strings): string {
  const template: Record<RowIssue, string> = {
    stock: strings.issueStock,
    final: strings.issueFinal,
    exceeds: strings.issueExceeds,
  };
  return t(template[issue], {
    name: name.trim() === "" ? strings.unnamed : name,
  });
}

function tableRows(plan: MixPlan, strings: Strings, fmt: NumberFormatter) {
  const unit = (u: keyof typeof PCR_UNIT_LABELS) => PCR_UNIT_LABELS[u];
  const rows = plan.rows.map((row) => [
    row.name,
    `${plain(fmt, row.stock)} ${unit(row.unit)}`,
    `${plain(fmt, row.final)} ${unit(row.unit)}`,
    vol(fmt, row.perReactionUl),
    vol(fmt, row.masterMixUl),
  ]);
  rows.push([
    strings.waterRow,
    "",
    "",
    vol(fmt, plan.waterPerReactionUl),
    vol(fmt, plan.waterMasterMixUl),
  ]);
  return rows;
}

function Notes({
  plan,
  volume,
  fmt,
}: {
  plan: MixPlan;
  volume: number;
  fmt: NumberFormatter;
}) {
  const strings = useToolUi(PCR_MASTER_MIX_CALCULATOR_STRINGS);
  const notes: string[] = plan.issues.map((i) =>
    issueText(i.issue, i.name, strings)
  );
  if (plan.overfullByUl > 0) {
    notes.push(
      t(strings.overfull, {
        over: vol(fmt, plan.overfullByUl),
        volume: plain(fmt, volume),
      })
    );
  }
  for (const row of plan.rows) {
    if (row.masterMixUl < SMALL_MIX_UL) {
      notes.push(
        t(strings.smallVolume, {
          name: row.name,
          value: vol(fmt, row.masterMixUl),
        })
      );
    }
  }
  return notes.map((note) => <Notice key={note}>{note}</Notice>);
}

function Steps({
  plan,
  volume,
  fmt,
}: {
  plan: MixPlan;
  volume: number;
  fmt: NumberFormatter;
}) {
  const strings = useToolUi(PCR_MASTER_MIX_CALCULATOR_STRINGS);
  const template =
    plan.templateUl > 0
      ? t(strings.stepTemplate, { template: vol(fmt, plan.templateUl) })
      : t(strings.stepNoTemplate, { volume: plain(fmt, volume) });
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
      <ol className="list-decimal space-y-1 ps-5 text-foreground text-sm">
        <li>
          {t(strings.stepMake, { total: vol(fmt, plan.masterMixTotalUl) })}
        </li>
        <li>
          {t(strings.stepDispense, {
            aliquot: vol(fmt, plan.aliquotUl),
            count: plain(fmt, plan.reactions),
          })}
        </li>
        <li>{template}</li>
      </ol>
    </div>
  );
}

function Results({ input, fmt }: { input: MixInput; fmt: NumberFormatter }) {
  const strings = useToolUi(PCR_MASTER_MIX_CALCULATOR_STRINGS);
  const outcome = planMasterMix(input);
  if ("error" in outcome) {
    return <ErrorNotice>{errorText(outcome.error, strings)}</ErrorNotice>;
  }
  const { plan } = outcome;
  if (plan.rows.length === 0 && plan.issues.length === 0) {
    return <Notice>{strings.noReagents}</Notice>;
  }
  const head = [
    strings.colName,
    strings.colStock,
    strings.colFinal,
    strings.colPerReaction,
    strings.colMasterMix,
  ];
  const rows = tableRows(plan, strings, fmt);
  const totalRow = [
    strings.totalRow,
    "",
    "",
    vol(fmt, plan.aliquotUl),
    vol(fmt, plan.masterMixTotalUl),
  ];
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={`${strings.masterMixTotal} (${strings.unitUl})`}
          value={vol(fmt, plan.masterMixTotalUl)}
        />
        <StatCard
          label={`${strings.aliquot} (${strings.unitUl})`}
          value={vol(fmt, plan.aliquotUl)}
        />
        <StatCard
          label={strings.reactionsMade}
          value={fmt(plan.effectiveReactions, { maximumFractionDigits: 2 })}
        />
      </StatGrid>
      <h3 className={WIDGET_H3}>{strings.recipeHeading}</h3>
      <DataTable head={head} rows={[...rows, totalRow]} />
      <Notes fmt={fmt} plan={plan} volume={input.volumeUl} />
      <Steps fmt={fmt} plan={plan} volume={input.volumeUl} />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={toTsv(head, [...rows, totalRow])}
      />
    </>
  );
}

export default function PcrMasterMixCalculatorWidget() {
  const strings = useToolUi(PCR_MASTER_MIX_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [preset, setPreset] = useState<PresetId>("taq");
  const [volume, setVolume] = useState("25");
  const [reactions, setReactions] = useState("8");
  const [overage, setOverage] = useState("10");
  const [template, setTemplate] = useState("1");
  const [rows, setRows] = useState<RowState[]>(() =>
    buildRows("taq", strings, fmt)
  );
  const [counter, setCounter] = useState(0);

  function choosePreset(next: PresetId) {
    setPreset(next);
    setRows(buildRows(next, strings, fmt));
    setVolume(plain(fmt, PCR_PRESETS[next].volumeUl));
    setTemplate(plain(fmt, PCR_PRESETS[next].templateUl));
    if (next === "custom") {
      addRow();
    }
  }
  function addRow() {
    setCounter((n) => n + 1);
    setRows((current) => [
      ...current,
      { id: `n${counter}`, name: "", stock: "", final: "", unit: "uM" },
    ]);
  }
  function updateRow(id: string, patch: Partial<RowState>) {
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }
  function removeRow(id: string) {
    setRows((current) => current.filter((row) => row.id !== id));
  }

  const volumeValue = parseMeasuredNumber(volume);
  const reactionsValue = parseMeasuredNumber(reactions);
  const overageValue = parseMeasuredNumber(overage);
  const templateValue = parseMeasuredNumber(template);
  const input: MixInput | null =
    volumeValue === null ||
    reactionsValue === null ||
    overageValue === null ||
    templateValue === null
      ? null
      : {
          volumeUl: volumeValue,
          reactions: reactionsValue,
          overagePct: overageValue,
          templateUl: templateValue,
          components: rows.map((row) => ({
            id: row.id,
            name: row.name,
            stock: parseMeasuredNumber(row.stock),
            final: parseMeasuredNumber(row.final),
            unit: row.unit,
          })),
        };

  return (
    <>
      <SelectField
        id={`${ID}-preset`}
        label={strings.presetLabel}
        onChange={choosePreset}
        options={[
          { value: "taq", label: strings.presetTaq },
          { value: "q5", label: strings.presetQ5 },
          { value: "custom", label: strings.presetCustom },
        ]}
        value={preset}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <TextField
          id={`${ID}-volume`}
          label={strings.reactionVolume}
          numeric
          onChange={setVolume}
          value={volume}
        />
        <TextField
          hint={strings.reactionsHint}
          id={`${ID}-n`}
          label={strings.reactions}
          numeric
          onChange={setReactions}
          value={reactions}
        />
        <TextField
          hint={strings.overageHint}
          id={`${ID}-overage`}
          label={strings.overage}
          numeric
          onChange={setOverage}
          value={overage}
        />
        <TextField
          hint={strings.templateHint}
          id={`${ID}-template`}
          label={strings.template}
          numeric
          onChange={setTemplate}
          value={template}
        />
      </div>
      <h2 className={WIDGET_H2}>{strings.reagentsHeading}</h2>
      <PcrComponentRows
        onAdd={addRow}
        onChange={updateRow}
        onRemove={removeRow}
        rows={rows}
        strings={{
          name: strings.colName,
          stock: strings.colStock,
          final: strings.colFinal,
          unit: strings.colUnit,
          remove: strings.remove,
          add: strings.addReagent,
          namePlaceholder: strings.namePlaceholder,
        }}
      />
      <p className="text-muted-foreground text-xs">{strings.sameUnitNote}</p>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {input ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <Results fmt={fmt} input={input} />
          </div>
        ) : (
          <Notice>{strings.enter}</Notice>
        )}
      </section>
    </>
  );
}
